import postService from '../services/posts';
import { createVoteThunks } from './voteThunks';
import { createReactionThunk } from './reactionThunks';
import mergeResultsById from '../utils/mergeResultsById';
import type { PostItem, ReduxAction, SearchState, VotePayload } from '../types/common';
import type { AppDispatch } from '../store';

const searchReducer = (
  state: SearchState | null = null,
  action: ReduxAction<unknown>
): SearchState | null => {
  switch (action.type) {
    case 'SET_SEARCH_RESULTS':
      return action.payload as SearchState;
    case 'TOGGLE_SEARCH_VOTE':
    case 'TOGGLE_SEARCH_REACTION':
      return {
        ...state,
        results: (state?.results || []).map((r) =>
          r.id !== (action.payload as VotePayload).id
            ? r
            : { ...r, ...((action.payload as VotePayload).data || {}) }
        ),
      } as SearchState;
    case 'LOAD_SEARCH_POSTS':
      return {
        ...(action.payload as SearchState),
        results: [
          ...(state?.results || []),
          ...(((action.payload as SearchState).results as unknown[]) || []),
        ],
        // The page payload carries no query — keep the stamped identity so the
        // returning fast path can recognize this state.
        query: state?.query,
      } as SearchState;
    case 'REFRESH_SEARCH_RESULTS':
      // Silent "returning" refresh: in-place field merge only, so a restored
      // scroll position cannot move (see mergeResultsById).
      return {
        ...state,
        results: mergeResultsById(
          (state?.results || []) as PostItem[],
          (((action.payload as SearchState).results as PostItem[]) || [])
        ),
      } as SearchState;
    default:
      return state;
  }
};

export const setSearchResults = (query: string) => {
  return async (dispatch: AppDispatch) => {
    const results = await postService.getSearchResults(query, 10, 1);

    dispatch({
      type: 'SET_SEARCH_RESULTS',
      // Stamp the query so a later visit can tell whether this state is "ours".
      payload: { ...results, query },
    });
  };
};

/** Background page-1 refresh for the returning fast path (see REFRESH_SEARCH_RESULTS). */
export const refreshSearchResults = (query: string) => {
  return async (dispatch: AppDispatch) => {
    const results = await postService.getSearchResults(query, 10, 1);

    dispatch({
      type: 'REFRESH_SEARCH_RESULTS',
      payload: results,
    });
  };
};

export const loadSearchPosts = (query: string, page: number) => {
  return async (dispatch: AppDispatch) => {
    const results = await postService.getSearchResults(query, 10, page);

    dispatch({
      type: 'LOAD_SEARCH_POSTS',
      payload: results,
    });
  };
};

export const { toggleUpvote, toggleDownvote } = createVoteThunks('TOGGLE_SEARCH_VOTE');
export const toggleReaction = createReactionThunk('TOGGLE_SEARCH_REACTION');

export default searchReducer;